import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { createBrowserRouter, type RouteObject } from "react-router";
import { RouterProvider } from "react-router/dom";

import { createApiClient } from "../api/client";
import type { ClientConfig } from "../api/types";
import type { AuthClient } from "../auth/authClient";
import { fetchClientConfig } from "../config/clientConfig";
import { markEnvironment } from "../config/environmentMark";
import { reloadPage } from "./reload";
import { shouldRetry } from "./retry";
import { routes } from "./routes";
import { LoadingScreen, MessageScreen, SignInScreen } from "./Screens";
import { useGoToMicrosoft } from "./goToMicrosoft";
import { SessionGate } from "./SessionGate";

type Router = ReturnType<typeof createBrowserRouter>;

export interface BootstrapDeps {
  loadConfig: () => Promise<ClientConfig>;
  createAuthClient: (config: ClientConfig) => Promise<AuthClient>;
  createRouter: (routes: RouteObject[]) => Router;
  fetchFn: typeof fetch;
  reload: () => void;
}

const defaultDeps: BootstrapDeps = {
  loadConfig: () => fetchClientConfig(),
  // MSAL is most of the bundle; loading it as its own chunk lets the loading
  // screen render first while MSAL downloads alongside /client-config.
  createAuthClient: async (config) => {
    const { createMsalAuthClient } = await import("../auth/authClient");
    return createMsalAuthClient(config);
  },
  createRouter: (r) => createBrowserRouter(r),
  fetchFn: (...args) => fetch(...args),
  reload: reloadPage,
};

interface Started {
  auth: AuthClient;
  freshSignIn: boolean;
}

type State =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "ready"; auth: AuthClient; freshSignIn: boolean };

/**
 * Starts the app: reads the runtime configuration, starts sign-in, and then
 * either asks the user to sign in or renders the signed-in app.
 */
export function Bootstrap({ deps: overrides = {} }: { deps?: Partial<BootstrapDeps> }) {
  const [deps] = useState<BootstrapDeps>(() => ({ ...defaultDeps, ...overrides }));
  const [state, setState] = useState<State>({ kind: "loading" });

  const [attempt, setAttempt] = useState(0);
  const signIn = useGoToMicrosoft();
  // One startup per attempt. React's StrictMode runs effects twice in
  // development; the cached promise means MSAL still starts only once, and the
  // fresh-sign-in flag is read exactly once.
  const startups = useRef(new Map<number, Promise<Started>>());

  useEffect(() => {
    let startup = startups.current.get(attempt);
    if (startup === undefined) {
      startup = deps
        .loadConfig()
        .then((config) => {
          markEnvironment(config.environment);
          return deps.createAuthClient(config);
        })
        .then((auth) => ({ auth, freshSignIn: auth.consumeFreshSignIn() }));
      startups.current.set(attempt, startup);
    }
    let cancelled = false;
    startup.then(
      ({ auth, freshSignIn }) => {
        if (!cancelled) {
          setState({ kind: "ready", auth, freshSignIn });
        }
      },
      () => {
        if (!cancelled) {
          setState({ kind: "failed" });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [deps, attempt]);

  const retry = () => {
    setState({ kind: "loading" });
    setAttempt((n) => n + 1);
  };

  if (state.kind === "loading") {
    return <LoadingScreen onReload={deps.reload} />;
  }
  if (state.kind === "failed") {
    return (
      <MessageScreen
        title="Can't reach Axon Time"
        message="The app could not start. Check your connection and try again."
        action={{ label: "Try again", onClick: retry }}
      />
    );
  }
  if (state.auth.account === null) {
    return (
      <SignInScreen
        failed={signIn.failed}
        onSignIn={() => {
          signIn.go(() => state.auth.signIn());
        }}
      />
    );
  }
  return <SignedInApp auth={state.auth} freshSignIn={state.freshSignIn} deps={deps} />;
}

function SignedInApp({
  auth,
  freshSignIn,
  deps,
}: {
  auth: AuthClient;
  freshSignIn: boolean;
  deps: BootstrapDeps;
}) {
  const api = useMemo(
    () =>
      createApiClient((fresh) => auth.getAccessToken(fresh), deps.fetchFn, undefined, {
        onUnauthorized: () => {
          auth.markRenewalNeeded();
        },
      }),
    [auth, deps],
  );
  const [queryClient] = useState(
    () => new QueryClient({ defaultOptions: { queries: { retry: shouldRetry } } }),
  );
  const [router] = useState(() => deps.createRouter(routes));

  return (
    <QueryClientProvider client={queryClient}>
      <SessionGate auth={auth} api={api} freshSignIn={freshSignIn}>
        <RouterProvider router={router} />
      </SessionGate>
    </QueryClientProvider>
  );
}
